import { useCallback, useEffect, useState } from 'react'
import { PageHead } from '@components/PageHead'
import { ToolsSubNav } from '@features/tools/components/ToolsSubNav'
import { useGeneratorHistory } from '@features/tools/hooks/useGeneratorHistory'
import { generatorHistoryStore } from '@features/tools/services/generatorHistoryStore'
import {
  calcEntropyBits,
  calcPassphraseEntropy,
  calcPoolSize,
  entropyToLabelKey,
  entropyToScore,
  generatePassphrase,
  generatePassword,
  generateUsername,
  type PassphraseOptions,
  type PasswordOptions,
  type UsernameOptions,
} from '@features/tools/services/passwordGenerator'
import { setPendingPassword } from '@features/tools/services/generatorHandoff'
import { copyToClipboard, getEffectiveClipboardTtlSec } from '@services/clipboard'
import { useToast } from '@shared/ui/useToast'
import { Icon } from '@/icons/Icon'
import { useTranslation } from '@/i18n'

/**
 * パスワード生成器 (T-D-Tools)。
 *
 * 設計判断:
 *   - crypto.getRandomValues を使用 (Math.random() は使わない)。
 *   - パスワードタブ / パスフレーズタブを PageHead.tabs で切り替え。
 *   - 強度カードは entropy bits から動的算出 (prototype の static 値を置き換え)。
 *   - コピー処理は clipboard.ts の copyToClipboard に委譲 (90 秒自動クリア保証)。
 */

const DEFAULT_PW_OPTS: PasswordOptions = {
  length: 20,
  useUpper: true,
  useLower: true,
  useNum: true,
  useSym: true,
}

const DEFAULT_PP_OPTS: PassphraseOptions = {
  wordCount: 6,
  separator: '-',
  includeNumber: true,
  capitalize: false,
}

const DEFAULT_UN_OPTS: UsernameOptions = {
  mode: 'word',
  wordCount: 2,
  capitalize: true,
  appendNumber: true,
  length: 12,
  emailDomain: 'gmail.com',
}

/** email モードのドメイン候補 (UI チップ)。先頭の `@` は表示用、保存値は剥がした形。 */
const EMAIL_DOMAIN_PRESETS: readonly string[] = [
  'gmail.com',
  'yahoo.com',
  'yahoo.co.jp',
  'outlook.com',
  'icloud.com',
  'hotmail.com',
]

type GeneratorTab = 'password' | 'passphrase' | 'username'

const SEPARATORS = [
  { value: '-', labelKey: 'tools.generator.passphrase.sep_hyphen' },
  { value: '_', labelKey: 'tools.generator.passphrase.sep_underscore' },
  { value: '.', labelKey: 'tools.generator.passphrase.sep_dot' },
  { value: ' ', labelKey: 'tools.generator.passphrase.sep_space' },
]

const colorize = (s: string): JSX.Element[] =>
  s.split('').map((c, i) => {
    if (/\d/u.test(c))
      return (
        <span key={i} className="num">
          {c}
        </span>
      )
    if (/[^a-zA-Z0-9]/u.test(c))
      return (
        <span key={i} className="sym">
          {c}
        </span>
      )

    return <span key={i}>{c}</span>
  })

export const GeneratorPage = (): JSX.Element => {
  const { t } = useTranslation()
  const { show: showToast } = useToast()
  const [tab, setTab] = useState<GeneratorTab>('password')
  const [pwOpts, setPwOpts] = useState<PasswordOptions>(DEFAULT_PW_OPTS)
  const [ppOpts, setPpOpts] = useState<PassphraseOptions>(DEFAULT_PP_OPTS)
  const [unOpts, setUnOpts] = useState<UsernameOptions>(DEFAULT_UN_OPTS)
  const [pwd, setPwd] = useState(() => generatePassword(DEFAULT_PW_OPTS))
  const [phrase, setPhrase] = useState(() => generatePassphrase(DEFAULT_PP_OPTS))
  const [username, setUsername] = useState(() => generateUsername(DEFAULT_UN_OPTS))
  const [historyOpen, setHistoryOpen] = useState(false)
  const [revealedHistoryIds, setRevealedHistoryIds] = useState<readonly string[]>([])

  const {
    entries: historyEntries,
    remove: removeHistory,
    clear: clearHistory,
  } = useGeneratorHistory()

  // 初回マウント分 + 値変化を履歴に push (重複は store 側で抑止)
  useEffect(() => {
    generatorHistoryStore.push('password', pwd)
  }, [pwd])

  useEffect(() => {
    generatorHistoryStore.push('passphrase', phrase)
  }, [phrase])

  useEffect(() => {
    generatorHistoryStore.push('username', username)
  }, [username])

  const regenPwd = useCallback(() => {
    setPwd(generatePassword(pwOpts))
  }, [pwOpts])

  const regenPhrase = useCallback(() => {
    setPhrase(generatePassphrase(ppOpts))
  }, [ppOpts])

  const regenUsername = useCallback(() => {
    setUsername(generateUsername(unOpts))
  }, [unOpts])

  const toggleRevealHistory = useCallback((id: string): void => {
    setRevealedHistoryIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
  }, [])

  const handleCopy = useCallback(
    async (value: string): Promise<void> => {
      try {
        await copyToClipboard(value)
        showToast({
          titleKey: 'vault.toast.copied_username',
          titleVars: { ttl: getEffectiveClipboardTtlSec() },
          tone: 'success',
          dedupe: true,
        })
      } catch {
        showToast({ titleKey: 'vault.toast.copy_failed', tone: 'danger', dedupe: true })
      }
    },
    [showToast],
  )

  const entropy = calcEntropyBits(pwOpts)
  const score = entropyToScore(entropy)
  const poolSize = calcPoolSize(pwOpts)

  const kindCount = [pwOpts.useUpper, pwOpts.useLower, pwOpts.useNum, pwOpts.useSym].filter(
    Boolean,
  ).length

  const ppEntropy = calcPassphraseEntropy(ppOpts)
  const ppScore = entropyToScore(ppEntropy)

  return (
    <div>
      <PageHead
        title={t(`tools.generator.title_${tab}`)}
        sub={t(`tools.generator.sub_${tab}`)}
        actions={
          <button
            type="button"
            className={`btn btn-ghost gen-history-toggle${historyOpen ? ' is-active' : ''}`}
            onClick={() => setHistoryOpen(v => !v)}
            aria-pressed={historyOpen}
            data-testid="generator-history-toggle"
          >
            <Icon name="clock" aria-hidden="true" />
            <span className="gen-history-label">
              {historyOpen ? t('tools.generator.history.hide') : t('tools.generator.history.show')}
            </span>
          </button>
        }
        beforeTabs={<ToolsSubNav active="tools/generator" />}
        tabs={[
          { key: 'password', label: t('tools.generator.tab_password') },
          { key: 'passphrase', label: t('tools.generator.tab_passphrase') },
          { key: 'username', label: t('tools.generator.tab_username') },
        ]}
        activeTab={tab}
        onTab={k => {
          setTab(k as GeneratorTab)
        }}
      />

      {historyOpen && (
        <div
          className="vault-delete-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="generator-history-title"
          onClick={ev => {
            if (ev.target === ev.currentTarget) setHistoryOpen(false)
          }}
        >
          <div
            className="vault-delete-dialog gen-history-dialog"
            data-testid="generator-history-panel"
          >
            <div className="gen-history-head">
              <div>
                <h3 id="generator-history-title" className="gen-history-title">
                  {t('tools.generator.history.title')}
                </h3>
                <div className="gen-history-intro">{t('tools.generator.history.intro')}</div>
              </div>
              <button
                type="button"
                className="icon-btn sm"
                onClick={() => setHistoryOpen(false)}
                aria-label={t('tools.generator.history.hide')}
                title={t('tools.generator.history.hide')}
                data-testid="generator-history-close"
              >
                ✕
              </button>
            </div>
            {historyEntries.length === 0 ? (
              <div className="gen-history-empty">{t('tools.generator.history.empty')}</div>
            ) : (
              <ul className="gen-history-list" role="list">
                {historyEntries.map(entry => {
                  const revealed = revealedHistoryIds.includes(entry.id)

                  return (
                    <li
                      key={entry.id}
                      className="gen-history-row"
                      data-testid={`generator-history-row-${entry.id}`}
                    >
                      <div className="gen-history-row-meta">
                        <span className="tag">
                          {t(`tools.generator.history.kind_${entry.kind}`)}
                        </span>
                        <span className="gen-history-row-value mono">
                          {revealed ? entry.value : '•'.repeat(Math.min(entry.value.length, 24))}
                        </span>
                      </div>
                      <div className="acts">
                        <button
                          type="button"
                          className="icon-btn sm"
                          onClick={() => toggleRevealHistory(entry.id)}
                          aria-label={
                            revealed
                              ? t('tools.generator.history.hide_value')
                              : t('tools.generator.history.show_value')
                          }
                          title={
                            revealed
                              ? t('tools.generator.history.hide_value')
                              : t('tools.generator.history.show_value')
                          }
                        >
                          <Icon name={revealed ? 'eye-off' : 'eye'} />
                        </button>
                        <button
                          type="button"
                          className="icon-btn sm"
                          onClick={() => void handleCopy(entry.value)}
                          aria-label={t('tools.generator.history.copy')}
                          title={t('tools.generator.history.copy')}
                        >
                          <Icon name="copy" />
                        </button>
                        <button
                          type="button"
                          className="icon-btn sm"
                          onClick={() => {
                            removeHistory(entry.id)
                            setRevealedHistoryIds(prev => prev.filter(x => x !== entry.id))
                          }}
                          aria-label={t('tools.generator.history.remove')}
                          title={t('tools.generator.history.remove')}
                          data-testid={`generator-history-remove-${entry.id}`}
                        >
                          <Icon name="trash" />
                        </button>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
            <div className="vault-delete-actions">
              {historyEntries.length > 0 && (
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    clearHistory()
                    setRevealedHistoryIds([])
                  }}
                  data-testid="generator-history-clear"
                >
                  <Icon name="trash" aria-hidden="true" />
                  {t('tools.generator.history.clear')}
                </button>
              )}
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setHistoryOpen(false)}
              >
                {t('tools.generator.history.hide')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="page-body">
        {tab === 'password' && (
          <div className="gen-layout">
            <div>
              <div className="gen-out">{colorize(pwd)}</div>
              <div className="gen-out-actions">
                <button type="button" className="btn btn-secondary" onClick={regenPwd}>
                  <Icon name="refresh" aria-hidden="true" />
                  {t('tools.generator.regen')}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => void handleCopy(pwd)}
                >
                  <Icon name="copy" aria-hidden="true" />
                  {t('tools.generator.copy')}
                </button>
                <div className="gen-out-actions-spacer" />
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setPendingPassword(pwd)
                    window.location.hash = 'vault/new'
                  }}
                >
                  <Icon name="save" aria-hidden="true" />
                  {t('tools.generator.use_item')}
                </button>
              </div>

              <div className="card gen-options-card">
                <h3 className="gen-options-title">{t('tools.generator.options.title')}</h3>
                <div className="slider-row">
                  <span className="l">{t('tools.generator.options.length')}</span>
                  <span className="v">{pwOpts.length}</span>
                </div>
                <input
                  type="range"
                  min={8}
                  max={64}
                  value={pwOpts.length}
                  onChange={ev => {
                    const next = { ...pwOpts, length: Number(ev.target.value) }

                    setPwOpts(next)
                    setPwd(generatePassword(next))
                  }}
                />
                <div className="gen-options-checks">
                  {(
                    [
                      { key: 'useUpper', labelKey: 'tools.generator.options.upper' },
                      { key: 'useLower', labelKey: 'tools.generator.options.lower' },
                      { key: 'useNum', labelKey: 'tools.generator.options.num' },
                      { key: 'useSym', labelKey: 'tools.generator.options.sym' },
                    ] as const
                  ).map(opt => (
                    <label
                      key={opt.key}
                      className={`check-row gen-check-opt${pwOpts[opt.key] ? ' gen-check-opt--active' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={pwOpts[opt.key]}
                        onChange={ev => {
                          const next = { ...pwOpts, [opt.key]: ev.target.checked }

                          setPwOpts(next)
                          setPwd(generatePassword(next))
                        }}
                      />
                      {t(opt.labelKey)}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-head">
                <h3>{t('tools.generator.strength.title')}</h3>
              </div>
              <div className="gen-strength-body">
                <div className={`strength s${score} gen-strength-bars`}>
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} className="seg" />
                  ))}
                </div>
                <div
                  className={`gen-strength-label${score <= 1 ? ' gen-strength-label--danger' : score === 2 ? ' gen-strength-label--warn' : ''}`}
                >
                  {t(entropyToLabelKey(entropy))}
                </div>
                <div className="gen-strength-stats">
                  {(
                    [
                      {
                        labelKey: 'tools.generator.strength.label_length',
                        value: `${pwOpts.length}${t('tools.generator.strength.unit_chars')}`,
                      },
                      {
                        labelKey: 'tools.generator.strength.label_entropy',
                        value: `${entropy}${t('tools.generator.strength.unit_bits')}`,
                      },
                      {
                        labelKey: 'tools.generator.strength.label_pool',
                        value: `${kindCount}${t('tools.generator.strength.unit_kinds')} (${poolSize})`,
                      },
                    ] as const
                  ).map(row => (
                    <div key={row.labelKey} className="gen-strength-stat">
                      <span className="gen-strength-stat-label">{t(row.labelKey)}</span>
                      <span className="gen-strength-stat-value mono">{row.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === 'passphrase' && (
          <div className="gen-passphrase">
            <div className="gen-out">{phrase}</div>
            <div className="gen-out-actions">
              <button type="button" className="btn btn-secondary" onClick={regenPhrase}>
                <Icon name="refresh" aria-hidden="true" />
                {t('tools.generator.regen')}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => void handleCopy(phrase)}
              >
                <Icon name="copy" aria-hidden="true" />
                {t('tools.generator.copy')}
              </button>
              <div className="gen-out-actions-spacer" />
              <div className="gen-strength-label-inline">
                <span className={`tag${ppScore >= 4 ? ' good' : ppScore >= 3 ? '' : ''}`}>
                  {t(entropyToLabelKey(ppEntropy))} {ppEntropy} bit
                </span>
              </div>
            </div>

            <div className="card gen-options-card">
              <div className="slider-row">
                <span className="l">{t('tools.generator.passphrase.word_count')}</span>
                <span className="v">{ppOpts.wordCount}</span>
              </div>
              <input
                type="range"
                min={3}
                max={10}
                value={ppOpts.wordCount}
                onChange={ev => {
                  const next = { ...ppOpts, wordCount: Number(ev.target.value) }

                  setPpOpts(next)
                  setPhrase(generatePassphrase(next))
                }}
              />

              <div className="field">
                <label className="field-label">{t('tools.generator.passphrase.separator')}</label>
                <select
                  className="select"
                  value={ppOpts.separator}
                  onChange={ev => {
                    const next = { ...ppOpts, separator: ev.target.value }

                    setPpOpts(next)
                    setPhrase(generatePassphrase(next))
                  }}
                >
                  {SEPARATORS.map(s => (
                    <option key={s.value} value={s.value}>
                      {t(s.labelKey)}
                    </option>
                  ))}
                </select>
              </div>

              <label className="check-row">
                <input
                  type="checkbox"
                  checked={ppOpts.includeNumber}
                  onChange={ev => {
                    const next = { ...ppOpts, includeNumber: ev.target.checked }

                    setPpOpts(next)
                    setPhrase(generatePassphrase(next))
                  }}
                />
                {t('tools.generator.passphrase.include_number')}
              </label>
              <label className="check-row">
                <input
                  type="checkbox"
                  checked={ppOpts.capitalize}
                  onChange={ev => {
                    const next = { ...ppOpts, capitalize: ev.target.checked }

                    setPpOpts(next)
                    setPhrase(generatePassphrase(next))
                  }}
                />
                {t('tools.generator.passphrase.capitalize')}
              </label>
            </div>
          </div>
        )}

        {tab === 'username' && (
          <div className="gen-passphrase">
            <div className="gen-out">{username}</div>
            <div className="gen-out-actions">
              <button type="button" className="btn btn-secondary" onClick={regenUsername}>
                <Icon name="refresh" aria-hidden="true" />
                {t('tools.generator.regen')}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => void handleCopy(username)}
              >
                <Icon name="copy" aria-hidden="true" />
                {t('tools.generator.copy')}
              </button>
            </div>

            <div className="card gen-options-card">
              <div className="field">
                <label className="field-label">{t('tools.generator.username.mode')}</label>
                <div className="seg-group" role="radiogroup" aria-label={t('tools.generator.username.mode')}>
                  {(['word', 'random', 'email'] as const).map(m => {
                    const active = unOpts.mode === m

                    return (
                      <button
                        key={m}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        className={`seg-btn${active ? ' seg-btn-active' : ''}`}
                        onClick={() => {
                          const next: UsernameOptions = { ...unOpts, mode: m }

                          setUnOpts(next)
                          setUsername(generateUsername(next))
                        }}
                      >
                        {t(`tools.generator.username.mode_${m}`)}
                      </button>
                    )
                  })}
                </div>
              </div>

              {unOpts.mode === 'word' && (
                <>
                  <div className="slider-row">
                    <span className="l">{t('tools.generator.username.word_count')}</span>
                    <span className="v">{unOpts.wordCount}</span>
                  </div>
                  <input
                    type="range"
                    min={1}
                    max={5}
                    value={unOpts.wordCount}
                    onChange={ev => {
                      const next = { ...unOpts, wordCount: Number(ev.target.value) }

                      setUnOpts(next)
                      setUsername(generateUsername(next))
                    }}
                  />
                  <label className="check-row">
                    <input
                      type="checkbox"
                      checked={unOpts.capitalize}
                      onChange={ev => {
                        const next = { ...unOpts, capitalize: ev.target.checked }

                        setUnOpts(next)
                        setUsername(generateUsername(next))
                      }}
                    />
                    {t('tools.generator.username.capitalize')}
                  </label>
                  <label className="check-row">
                    <input
                      type="checkbox"
                      checked={unOpts.appendNumber}
                      onChange={ev => {
                        const next = { ...unOpts, appendNumber: ev.target.checked }

                        setUnOpts(next)
                        setUsername(generateUsername(next))
                      }}
                    />
                    {t('tools.generator.username.append_number')}
                  </label>
                </>
              )}

              {unOpts.mode === 'random' && (
                <>
                  <div className="slider-row">
                    <span className="l">{t('tools.generator.username.length')}</span>
                    <span className="v">{unOpts.length}</span>
                  </div>
                  <input
                    type="range"
                    min={6}
                    max={32}
                    value={unOpts.length}
                    onChange={ev => {
                      const next = { ...unOpts, length: Number(ev.target.value) }

                      setUnOpts(next)
                      setUsername(generateUsername(next))
                    }}
                  />
                </>
              )}

              {unOpts.mode === 'email' && (
                <>
                  <div className="slider-row">
                    <span className="l">{t('tools.generator.username.length')}</span>
                    <span className="v">{unOpts.length}</span>
                  </div>
                  <input
                    type="range"
                    min={6}
                    max={32}
                    value={unOpts.length}
                    onChange={ev => {
                      const next = { ...unOpts, length: Number(ev.target.value) }

                      setUnOpts(next)
                      setUsername(generateUsername(next))
                    }}
                  />
                  <div className="field">
                    <label className="field-label" htmlFor="email-domain-input">
                      {t('tools.generator.username.email_domain')}
                    </label>
                    <div className="email-domain-input-row">
                      <span className="email-domain-prefix" aria-hidden="true">
                        @
                      </span>
                      <input
                        id="email-domain-input"
                        type="text"
                        className="input email-domain-input"
                        value={unOpts.emailDomain}
                        placeholder={t('tools.generator.username.email_domain_placeholder')}
                        autoComplete="off"
                        spellCheck={false}
                        onChange={ev => {
                          // 先頭 @ をユーザーが付けても剥がして保持 (生成側でも sanitize するが UX のため)
                          const raw = ev.target.value.replace(/^@+/, '')
                          const next = { ...unOpts, emailDomain: raw }

                          setUnOpts(next)
                          setUsername(generateUsername(next))
                        }}
                      />
                    </div>
                    <p className="field-help">{t('tools.generator.username.email_domain_hint')}</p>
                  </div>
                  <div className="field">
                    <label className="field-label">
                      {t('tools.generator.username.email_preset_label')}
                    </label>
                    <div className="chips">
                      {EMAIL_DOMAIN_PRESETS.map(d => {
                        const active = unOpts.emailDomain === d

                        return (
                          <button
                            key={d}
                            type="button"
                            className={`chip${active ? ' active' : ''}`}
                            onClick={() => {
                              const next = { ...unOpts, emailDomain: d }

                              setUnOpts(next)
                              setUsername(generateUsername(next))
                            }}
                          >
                            @{d}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
